import * as Sentry from '@sentry/browser';
import { first, isEmpty } from 'lodash';
import { apolloErrorFingerprint, isApolloErrorLike } from '@shared/api/graphqlErrorFingerprint';

/**
 * Backend GraphQL errors reach Sentry twice: once from `errorLoggerLink`, and once as the
 * `ApolloError` Apollo throws back to the query/mutation caller, which catch blocks report
 * without a fingerprint. This hook settles both halves of that.
 *
 * The caller-side copy is dropped: the link reports the same error with the operation name
 * and variables this copy lacks, and does so even when the caller swallows it. What is left
 * is ApolloErrors the link only breadcrumbs (network and client errors), plus unhandled
 * rejections — nothing caught those, which the link's report cannot tell us. Both share the
 * same all-node_modules stack, so they get a fingerprint to keep Sentry from collapsing
 * unrelated errors into one issue.
 *
 * Events that already carry a fingerprint are deliberate reports and are left alone.
 */
export function beforeSend(event: Sentry.Event, hint: Sentry.EventHint): Sentry.Event | null {
  const error = hint.originalException;
  if (!isEmpty(event.fingerprint) || !isApolloErrorLike(error)) {
    return event;
  }
  const unhandled = isUnhandled(event);
  if (!isEmpty(error.graphQLErrors) && !unhandled) {
    return null;
  }
  return { ...event, fingerprint: apolloErrorFingerprint(error, { unhandled }) };
}

function isUnhandled(event: Sentry.Event): boolean {
  return first(event.exception?.values)?.mechanism?.handled === false;
}
